import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { NumericInput } from "components/NumericInput/NumericInput";
import {
  CHAT_QNA_FIELD_TYPE,
  ChatQnAFieldType,
} from "constants/chatQnAFieldType";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useToast } from "hooks/useToast";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  getUpdateChatQNAAnswerCacheKey,
  useUpdateChatQNAMutation,
} from "store/apis/chatQnA";
import {
  AutofillFieldMetadata,
  FieldMetadata,
  FileUploadFieldMetadata,
  RadioFieldMetadata,
  SelectFieldMetadata,
} from "types/Models/chatQnA";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { AutofillAnswerCombobox } from "./AutofillAnswerCombobox";
import { ForeignSubsidiaryAnswerCombobox } from "./ForeignSubsidiaryAnswerCombobox";
import { DocumentUploadCell } from "./DocumentUploadCell";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Input from "components/DesignSystem/Input/Input";

type AnswerCellProps = {
  uuid: string;
  requestId: string;
  isSubmitted: boolean;
  groupId: string;
  initialAnswer?: string;
  fieldMetadata?: FieldMetadata | null;
  onSave?: () => void;
  onFocus?: () => void;
};

const AUTOFILL_TYPES = new Set<ChatQnAFieldType>([
  CHAT_QNA_FIELD_TYPE.SELECT_AUTOFILL,
  CHAT_QNA_FIELD_TYPE.SELECT_SHAREHOLDER_AUTOFILL,
  CHAT_QNA_FIELD_TYPE.SELECT_AUTHORIZED_SIGNATORY_AUTOFILL,
]);

const FOREIGN_SUBSIDIARY_NAME_FIELD_KEY = "foreign_subsidiary_name";

const DEFAULT_PLACEHOLDER = "Write down your answer here";
const MIN_ANSWER_ROWS = 3;
const CHARS_PER_ANSWER_ROW = 60;
const DEFAULT_SELECT_PLACEHOLDER = "Select...";

export const FILING_CHOICE_OPTIONS = [
  { value: "WITH_INKLE", label: "File with Inkle" },
  {
    value: "EXTERNAL",
    label: "File externally and upload the filed copy once it's done",
  },
];

export const AnswerCell = React.memo(
  ({
    uuid,
    requestId,
    isSubmitted,
    groupId,
    initialAnswer,
    fieldMetadata,
    onSave,
    onFocus,
  }: AnswerCellProps) => {
    const { alertToast } = useToast();
    const [updateAnswer] = useUpdateChatQNAMutation({
      fixedCacheKey: getUpdateChatQNAAnswerCacheKey(requestId),
    });
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    const isDisabled = isSubmitted;

    const [localValue, setLocalValue] = useState(initialAnswer ?? "");

    const initialValues = useRef({ [uuid]: initialAnswer ?? "" });

    const saveAnswer = useCallback(
      async (requestedAnswer: string) => {
        if (!groupId) return;
        try {
          await updateAnswer({
            answerRequestId: uuid,
            requestedAnswer,
            requestId,
            groupId,
          }).unwrap();
          onSaveRef.current?.();
        } catch (error) {
          alertToast({
            message:
              (error as BackendError)?.data?.error?.message ||
              (error as string),
          });
        }
      },
      [groupId, updateAnswer, uuid, requestId, alertToast]
    );

    const debouncedSaveAnswer = useMemo(
      () => debounce(saveAnswer),
      [saveAnswer]
    );

    if (fieldMetadata && AUTOFILL_TYPES.has(fieldMetadata.type)) {
      return (
        <AutofillAnswerCombobox
          requestId={requestId}
          uuid={uuid}
          groupId={groupId}
          fieldMetadata={fieldMetadata as AutofillFieldMetadata}
          currentAnswer={initialAnswer ?? null}
          isDisabled={isDisabled}
          onSave={onSave}
        />
      );
    }

    if (fieldMetadata?.field_key === FOREIGN_SUBSIDIARY_NAME_FIELD_KEY) {
      return (
        <ForeignSubsidiaryAnswerCombobox
          uuid={uuid}
          requestId={requestId}
          groupId={groupId}
          currentAnswer={initialAnswer ?? null}
          isDisabled={isDisabled}
          onSave={onSave}
        />
      );
    }

    if (fieldMetadata?.type === CHAT_QNA_FIELD_TYPE.RADIO) {
      const { options } = fieldMetadata as RadioFieldMetadata;

      const handleRadioChange = (value: string) => {
        setLocalValue(value);
        void saveAnswer(value);
      };

      return (
        <RadioGroup.Root
          value={localValue || undefined}
          onValueChange={handleRadioChange}
          disabled={isDisabled}
        >
          <RadioGroup.Content className="!t-flex-row t-gap-5 t-items-start">
            {options.map((opt) => (
              <RadioGroup.Item
                className="!t-w-auto"
                id={`${fieldMetadata.field_key}:${opt.value}`}
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Content>
        </RadioGroup.Root>
      );
    }

    if (fieldMetadata?.type === CHAT_QNA_FIELD_TYPE.FILING_CHOICE) {
      const handleFilingChange = (value: string) => {
        setLocalValue(value);
        void saveAnswer(value);
      };

      return (
        <RadioGroup.Root
          value={localValue || undefined}
          onValueChange={handleFilingChange}
          disabled={isDisabled}
        >
          <RadioGroup.Content>
            {FILING_CHOICE_OPTIONS.map((opt) => (
              <RadioGroup.Item
                id={`${fieldMetadata.field_key}:${opt.value}`}
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </RadioGroup.Item>
            ))}
          </RadioGroup.Content>
        </RadioGroup.Root>
      );
    }

    if (fieldMetadata?.type === CHAT_QNA_FIELD_TYPE.FILE_UPLOAD) {
      const allowMultiple = !(fieldMetadata as FileUploadFieldMetadata)
        .allow_single_upload;
      return (
        <DocumentUploadCell
          answerRequestId={uuid}
          groupId={groupId}
          isDisabled={isDisabled}
          isSubmitted={isSubmitted}
          allowMultiple={allowMultiple}
          onUploadSuccess={onSave}
        />
      );
    }

    if (fieldMetadata?.type === CHAT_QNA_FIELD_TYPE.NUMBER) {
      const numberInitialValues = { [uuid]: localValue };

      const handleNumberChange = ({ value }: { value: string }) => {
        setLocalValue(value);
        debouncedSaveAnswer(value);
      };

      return (
        <FormikForm initialValues={numberInitialValues} onSubmit={() => {}}>
          {() => (
            <NumericInput
              fieldProps={{ name: uuid }}
              numericProps={{
                onValueChange: handleNumberChange,
                placeholder: fieldMetadata.placeholder || DEFAULT_PLACEHOLDER,
                disabled: isDisabled,
              }}
            />
          )}
        </FormikForm>
      );
    }

    if (fieldMetadata?.type === CHAT_QNA_FIELD_TYPE.DATE) {
      const dateInitialValues = { [uuid]: localValue };

      const handleDateChange = (date: Date) => {
        const formatted = dayjs(date).format(YYYY_MM_DD);
        setLocalValue(formatted);
        void saveAnswer(formatted);
      };

      return (
        <FormikForm initialValues={dateInitialValues} onSubmit={() => {}}>
          {() => (
            <DateField
              portalId={uuid}
              name={uuid}
              label=""
              onDateChange={handleDateChange}
              disabled={isDisabled}
              block
            />
          )}
        </FormikForm>
      );
    }

    if (fieldMetadata?.type === CHAT_QNA_FIELD_TYPE.SELECT) {
      const selectOptions = (fieldMetadata as SelectFieldMetadata).options.map(
        (v) => ({ label: v, value: v })
      );
      const selectedOption =
        selectOptions.find((o) => o.value === localValue) ?? null;

      const handleSelectChange = (
        option: SingleValue<OptionData> | MultiValue<OptionData>
      ) => {
        if (!option || Array.isArray(option)) return;
        const value = (option as OptionData).value;
        setLocalValue(value);
        void saveAnswer(value);
      };

      return (
        <Combobox
          options={selectOptions}
          value={selectedOption}
          onChange={handleSelectChange}
          isDisabled={isDisabled}
          placeholder={fieldMetadata.placeholder || DEFAULT_SELECT_PLACEHOLDER}
          menuPortalTarget={document.body}
          menuPlacement="auto"
        />
      );
    }

    if (fieldMetadata) {
      return (
        <FormikForm initialValues={initialValues.current} onSubmit={() => {}}>
          <Input
            withForm
            label=""
            name={uuid}
            placeholder={fieldMetadata.placeholder || DEFAULT_PLACEHOLDER}
            required
            onChange={(e) => debouncedSaveAnswer(e.target.value)}
            onFocus={onFocus}
            disabled={isDisabled}
          />
        </FormikForm>
      );
    }

    const handleAnswerInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    const answerLineCount = (initialAnswer ?? "")
      .split("\n")
      .reduce(
        (total, line) =>
          total + Math.max(1, Math.ceil(line.length / CHARS_PER_ANSWER_ROW)),
        0
      );
    const answerRows = Math.max(MIN_ANSWER_ROWS, answerLineCount);

    return (
      <FormikForm initialValues={initialValues.current} onSubmit={() => {}}>
        {() => (
          <TextArea
            label=""
            name={uuid}
            placeholder={DEFAULT_PLACEHOLDER}
            required
            onChange={(e) => debouncedSaveAnswer(e.target.value)}
            onInput={handleAnswerInput}
            onFocus={onFocus}
            disabled={isDisabled}
            rows={answerRows}
            size="small"
            autoResize={false}
            style={{ overflowY: "hidden" }}
            isCopyable
          />
        )}
      </FormikForm>
    );
  }
);
